export type SetupState = "uninitialized" | "setup_in_progress" | "initialized" | "locked"

export interface SetupField {
  key: string
  label: string
  type:
    | "text"
    | "textarea"
    | "email"
    | "url"
    | "domain"
    | "hostname"
    | "ip"
    | "number"
    | "boolean"
    | "color"
    | "select"
    | "secretRef"
    | "fileOrUrl"
  required: boolean
  default?: string | number | boolean
  options?: string[]
}

export interface SetupSection {
  id: string
  title: string
  description: string
  fields: SetupField[]
}

export interface SetupStatus {
  state: SetupState
  requiresSetup: boolean
  draftExists: boolean
  schemaVersion: string
  message?: string | null
}

export interface SetupDraftResponse {
  state: SetupState
  draft: Record<string, unknown>
  missingRequired: string[]
  savedAt?: string | null
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1"

export const SETUP_SECTIONS: SetupSection[] = [
  {
    id: "organization",
    title: "Organisation",
    description: "Grunddaten des Kunden und der ERP-Instanz.",
    fields: [
      { key: "organization.name", label: "Organisationsname", type: "text", required: true },
      { key: "organization.productName", label: "Produktname", type: "text", required: true, default: "NetzERP" },
      { key: "organization.primaryDomain", label: "Primaere Domain", type: "domain", required: true },
      { key: "organization.adminName", label: "Initialer Admin-Name", type: "text", required: true },
      { key: "organization.adminEmail", label: "Initiale Admin-E-Mail", type: "email", required: true },
      { key: "organization.timezone", label: "Zeitzone", type: "text", required: true, default: "Europe/Berlin" },
      { key: "organization.locale", label: "Sprache/Locale", type: "text", required: true, default: "de-DE" },
      { key: "organization.supportEmail", label: "Support-E-Mail", type: "email", required: false },
    ],
  },
  {
    id: "dns",
    title: "Domains und DNS",
    description: "DNS-Zone, Ziel-IP und benoetigte Subdomains.",
    fields: [
      { key: "dns.provider", label: "DNS-Provider", type: "select", required: true, options: ["ionos", "cloudflare", "hetzner", "manual"] },
      { key: "dns.zone", label: "DNS-Zone", type: "domain", required: true },
      { key: "dns.apiCredentialRef", label: "DNS API Credential Referenz", type: "secretRef", required: true },
      { key: "dns.productionIp", label: "Produktions-IP", type: "ip", required: true },
      { key: "dns.stageIp", label: "Stage-IP", type: "ip", required: false },
      { key: "dns.subdomains.erp", label: "ERP Subdomain", type: "hostname", required: true, default: "erp" },
      { key: "dns.subdomains.id", label: "Logto Subdomain", type: "hostname", required: true, default: "id" },
      { key: "dns.subdomains.idAdmin", label: "Logto Admin Subdomain", type: "hostname", required: true, default: "id-admin" },
      { key: "dns.subdomains.cloud", label: "OpenCloud Subdomain", type: "hostname", required: true, default: "cloud" },
      { key: "dns.subdomains.mail", label: "Mail Subdomain", type: "hostname", required: true, default: "mail" },
      { key: "dns.subdomains.git", label: "Git Subdomain", type: "hostname", required: true, default: "git" },
      { key: "dns.subdomains.deploy", label: "Deployment Subdomain", type: "hostname", required: true, default: "deploy" },
      { key: "dns.subdomains.opencami", label: "OpenCami Subdomain", type: "hostname", required: true, default: "opencami" },
    ],
  },
  {
    id: "servers",
    title: "Server und Infrastruktur",
    description: "Zielserver, SSH-Zugriff und IONOS Cloud Parameter.",
    fields: [
      { key: "servers.provider", label: "Server-Provider", type: "select", required: true, options: ["ionos-cloud", "ionos-vps", "hetzner-cloud", "manual"] },
      { key: "servers.productionHost", label: "Produktionsserver Host/IP", type: "text", required: true },
      { key: "servers.productionSshUser", label: "Produktionsserver SSH-User", type: "text", required: true, default: "root" },
      { key: "servers.productionSshKeyRef", label: "Produktionsserver SSH-Key Referenz", type: "secretRef", required: true },
      { key: "servers.deploymentHost", label: "Deployment/Coolify Host/IP", type: "text", required: true },
      { key: "servers.stageHost", label: "Stage Host/IP", type: "text", required: false },
      { key: "servers.ionosCloudApiTokenRef", label: "IONOS Cloud API Token Referenz", type: "secretRef", required: true },
      { key: "servers.ionosDataCenter", label: "IONOS Datacenter", type: "text", required: true, default: "de/fra" },
      { key: "servers.defaultServerSize", label: "Default Servergroesse", type: "text", required: true, default: "2 vCPU / 4 GB RAM / 80 GB SSD" },
    ],
  },
  {
    id: "identity",
    title: "Identity, ERP und Dienste",
    description: "Logto, ERP, Mail, OpenCloud, Forgejo, Storage, OpenCami und 1Password.",
    fields: [
      { key: "logto.endpoint", label: "Logto Endpoint", type: "url", required: true },
      { key: "logto.adminEndpoint", label: "Logto Admin Endpoint", type: "url", required: true },
      { key: "logto.appId", label: "ERP Logto App ID", type: "text", required: true },
      { key: "logto.appSecretRef", label: "ERP Logto App Secret Referenz", type: "secretRef", required: true },
      { key: "logto.apiResource", label: "ERP API Resource", type: "url", required: true },
      { key: "logto.managementAppId", label: "Logto Management App ID", type: "text", required: true },
      { key: "logto.managementSecretRef", label: "Logto Management Secret Referenz", type: "secretRef", required: true },
      { key: "logto.postLogoutRedirectUri", label: "Post Logout Redirect URI", type: "url", required: true },
      { key: "erp.publicUrl", label: "ERP Public URL", type: "url", required: true },
      { key: "erp.apiUrl", label: "ERP API URL", type: "url", required: true },
      { key: "erp.databaseUrlRef", label: "ERP Database URL Referenz", type: "secretRef", required: true },
      { key: "erp.redisUrlRef", label: "ERP Redis URL Referenz", type: "secretRef", required: true },
      { key: "erp.secretKeyRef", label: "ERP Secret Key Referenz", type: "secretRef", required: true },
      { key: "erp.encryptionKeyRef", label: "ERP Encryption Key Referenz", type: "secretRef", required: true },
      { key: "erp.initialAdminEmail", label: "Initialer ERP Admin", type: "email", required: true },
      { key: "erp.corsOrigins", label: "CORS Origins", type: "textarea", required: true },
      { key: "mail.domain", label: "Mail-Domain", type: "domain", required: true },
      { key: "mail.publicUrl", label: "Stalwart Public URL", type: "url", required: true },
      { key: "mail.jmapUrl", label: "JMAP URL", type: "url", required: true },
      { key: "mail.adminUser", label: "Stalwart Admin User", type: "text", required: true },
      { key: "mail.adminPasswordRef", label: "Stalwart Admin Passwort Referenz", type: "secretRef", required: true },
      { key: "mail.defaultQuotaMb", label: "Default Quota MB", type: "number", required: true, default: 5120 },
      { key: "mail.appPasswordPolicy", label: "App-Passwort Policy", type: "text", required: true, default: "required-for-imap-smtp" },
      { key: "opencloud.url", label: "OpenCloud URL", type: "url", required: true },
      { key: "opencloud.webdavRootUrl", label: "OpenCloud WebDAV Root URL", type: "url", required: true },
      { key: "opencloud.adminUser", label: "OpenCloud Admin User", type: "text", required: true },
      { key: "opencloud.adminPasswordRef", label: "OpenCloud Admin/App-Token Referenz", type: "secretRef", required: true },
      { key: "opencloud.logtoIssuer", label: "OpenCloud Logto Issuer", type: "url", required: true },
      { key: "forgejo.url", label: "Forgejo URL", type: "url", required: true },
      { key: "forgejo.adminUser", label: "Forgejo Admin User", type: "text", required: true },
      { key: "forgejo.adminPasswordRef", label: "Forgejo Admin Passwort Referenz", type: "secretRef", required: true },
      { key: "forgejo.oidcClientId", label: "Forgejo OIDC Client ID", type: "text", required: true },
      { key: "forgejo.oidcClientSecretRef", label: "Forgejo OIDC Secret Referenz", type: "secretRef", required: true },
      { key: "forgejo.runnerTokenRef", label: "Forgejo Runner Token Referenz", type: "secretRef", required: false },
      { key: "storage.provider", label: "Storage Provider", type: "select", required: true, options: ["minio", "ionos-s3", "aws-s3", "manual"] },
      { key: "storage.endpoint", label: "S3 Endpoint", type: "url", required: true },
      { key: "storage.region", label: "S3 Region", type: "text", required: true, default: "eu-central-1" },
      { key: "storage.erpBucket", label: "ERP Bucket", type: "text", required: true },
      { key: "storage.brandingBucket", label: "Branding Bucket", type: "text", required: true },
      { key: "storage.accessKeyRef", label: "S3 Access Key Referenz", type: "secretRef", required: true },
      { key: "storage.secretKeyRef", label: "S3 Secret Key Referenz", type: "secretRef", required: true },
      { key: "opencami.publicUrl", label: "OpenCami Public URL", type: "url", required: true },
      { key: "opencami.gatewayUrl", label: "OpenClaw Gateway URL", type: "url", required: true },
      { key: "opencami.adminTokenRef", label: "OpenCami Admin Token Referenz", type: "secretRef", required: true },
      { key: "opencami.embedOnly", label: "Nur aus ERP erreichbar", type: "boolean", required: true, default: true },
      { key: "opencami.requiredRole", label: "Erforderliche ERP-Rolle", type: "text", required: true, default: "admin" },
      { key: "onepassword.accountUrl", label: "1Password Account URL", type: "url", required: true },
      { key: "onepassword.serviceAccountTokenRef", label: "Service Account Token Referenz", type: "secretRef", required: true },
      { key: "onepassword.platformVault", label: "Platform Vault", type: "text", required: true },
      { key: "onepassword.customerVaultPrefix", label: "Kunden-Vault Prefix", type: "text", required: true },
      { key: "onepassword.sshKeyItem", label: "SSH-Key Item", type: "text", required: true },
    ],
  },
  {
    id: "branding",
    title: "Branding",
    description: "Farben, Logos und zentrale Darstellung.",
    fields: [
      { key: "branding.brandName", label: "Brand Name", type: "text", required: true },
      { key: "branding.slogan", label: "Slogan", type: "text", required: true },
      { key: "branding.primaryColor", label: "Primaerfarbe", type: "color", required: true, default: "#94c900" },
      { key: "branding.accentColor", label: "Akzentfarbe", type: "color", required: true, default: "#f2c400" },
      { key: "branding.backgroundColor", label: "Hintergrundfarbe", type: "color", required: true, default: "#fbfcf8" },
      { key: "branding.foregroundColor", label: "Textfarbe", type: "color", required: true, default: "#151713" },
      { key: "branding.logoMain", label: "Logo Hauptvariante", type: "fileOrUrl", required: true },
      { key: "branding.logoLogin", label: "Logo Login", type: "fileOrUrl", required: true },
      { key: "branding.logoSmall", label: "Logo kompakt", type: "fileOrUrl", required: true },
      { key: "branding.logoBw", label: "Logo Schwarz-Weiss", type: "fileOrUrl", required: true },
      { key: "branding.favicon", label: "Favicon", type: "fileOrUrl", required: true },
    ],
  },
]

export const allSetupFields = SETUP_SECTIONS.flatMap((section) => section.fields)
export const requiredSetupFields = allSetupFields.filter((field) => field.required)

export function getNestedValue(source: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((current, part) => {
    if (!current || typeof current !== "object") return undefined
    return (current as Record<string, unknown>)[part]
  }, source)
}

export function setNestedValue(source: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const next = structuredClone(source)
  const parts = path.split(".")
  let current: Record<string, unknown> = next
  parts.slice(0, -1).forEach((part) => {
    if (!current[part] || typeof current[part] !== "object") current[part] = {}
    current = current[part] as Record<string, unknown>
  })
  current[parts[parts.length - 1]] = value
  return next
}

export function applySetupDefaults(draft: Record<string, unknown>): Record<string, unknown> {
  return allSetupFields.reduce((current, field) => {
    const existing = getNestedValue(current, field.key)
    if (existing !== undefined || field.default === undefined) return current
    return setNestedValue(current, field.key, field.default)
  }, draft)
}

export function getMissingRequired(draft: Record<string, unknown>): string[] {
  return requiredSetupFields
    .filter((field) => {
      const value = getNestedValue(draft, field.key)
      return value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)
    })
    .map((field) => field.key)
}

async function setupRequest<T>(path: string, options: RequestInit = {}, setupToken?: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/setup${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(setupToken ? { "X-Setup-Token": setupToken } : {}),
      ...(options.headers ?? {}),
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error?.detail?.message || error?.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

export const setupApi = {
  status: () => setupRequest<SetupStatus>("/status"),
  draft: (setupToken: string) => setupRequest<SetupDraftResponse>("/draft", {}, setupToken),
  saveDraft: (draft: Record<string, unknown>, setupToken: string) =>
    setupRequest<SetupDraftResponse>("/draft", { method: "PUT", body: JSON.stringify({ draft }) }, setupToken),
  complete: (draft: Record<string, unknown>, setupToken: string) =>
    setupRequest<{ state: SetupState; completedAt: string }>("/complete", { method: "POST", body: JSON.stringify({ draft }) }, setupToken),
}
